import React, { useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { Loader2, Boxes, Gamepad2, Server as ServerIco } from 'lucide-react';
import { apiFetch } from '@/utils/api';
import { serverStore } from '@/stores/server.store';

interface ServerIconProps {
  serverId: string;
  /** Server type — used to pick a sensible default glyph when no custom/modpack icon exists. */
  type?: string;
  size?: number;
  className?: string;
  fallbackClassName?: string;
}

/** Family for a stored server `type` (e.g. "vanilla", "fabric", "steam:892970"). */
const familyOf = (type?: string): 'java' | 'bedrock' | 'steam' | 'modpack' => {
  const t = String(type || 'vanilla').toLowerCase();
  if (t === 'bedrock' || t === 'minecraft-bedrock') return 'bedrock';
  if (t === 'steam' || t.startsWith('steam:')) return 'steam';
  if (t === 'modpack') return 'modpack';
  return 'java';
};

/** A crisp, recognizable default glyph per game family (no trademarked assets). */
export const GameGlyph = observer(({ type, size, className = '' }: { type?: string; size: number; className?: string }) => {
  const family = familyOf(type);
  const box = `flex items-center justify-center rounded-lg overflow-hidden ${className}`;
  const style = { width: size, height: size } as const;
  const [steamImgFailed, setSteamImgFailed] = useState(false);

  // Ensure the catalog is loaded so we can map install→store AppID for the image.
  useEffect(() => { if (family === 'steam') serverStore.loadSteamCatalogOnce(); }, [family]);

  if (family === 'steam') {
    // Show the real Steam game header image; the dedicated-server AppID often has no
    // image, so resolve the store AppID via the catalog. Fall back to a gamepad glyph.
    const installAppId = String(type || '').match(/^steam:(\d+)$/)?.[1];
    const imgAppId = installAppId ? serverStore.steamStoreAppId(Number(installAppId)) : null;
    if (imgAppId && !steamImgFailed) {
      return (
        <img
          src={`https://cdn.cloudflare.steamstatic.com/steam/apps/${imgAppId}/header.jpg`}
          alt="Steam Game"
          className={`rounded-lg object-cover ${className}`}
          style={style}
          onError={() => setSteamImgFailed(true)}
        />
      );
    }
    return (
      <div className={`${box} bg-gradient-to-br from-[#2a3f5f] to-[#1b2838]`} style={style}>
        <Gamepad2 size={size * 0.52} className="text-sky-300" />
      </div>
    );
  }

  if (family === 'modpack')
    return (
      <div className={`${box} bg-gradient-to-br from-ember/25 to-ember/5`} style={style}>
        <Boxes size={size * 0.52} className="text-ember" />
      </div>
    );

  // Minecraft: eigener 3D-Pixel-Grasblock (Java grün/Erde, Bedrock türkis/Stein).
  return (
    <div className={`${box} bg-gradient-to-br from-white/[0.07] to-white/[0.02]`} style={style}>
      <GrassBlock size={Math.round(size * 0.84)} variant={family === 'bedrock' ? 'bedrock' : 'java'} />
    </div>
  );
});

// ---------- 3D-Grasblock (selbst gezeichnet, keine Mojang-Grafik) ----------

const PALETTES = {
  java: { grass: ['#5fa83a', '#6cbf3f', '#7cca49', '#4d9230'], dirt: ['#8a5a3b', '#7a4e33', '#99684a', '#684128'] },
  bedrock: { grass: ['#3aa76d', '#46b87b', '#56c489', '#2d8a58'], dirt: ['#6b7280', '#5d6471', '#7c8390', '#4a505b'] },
} as const;

/** Gleichbleibendes „Zufalls"-Muster, damit der Block überall gleich aussieht. */
const pixelGrid = (seed: number, pick: (y: number, r: number) => number) => {
  let s = seed;
  const next = () => (s = (s * 16807) % 2147483647) / 2147483647;
  return Array.from({ length: 8 }, (_, y) => Array.from({ length: 8 }, () => pick(y, next())));
};
// Werte < 10 = Gras-Ton, ab 10 = Erd-Ton (Wert - 10).
const TOP_FACE = pixelGrid(7, (_y, r) => (r < 0.42 ? 1 : r < 0.68 ? 0 : r < 0.87 ? 2 : 3));
const sideFace = (seed: number) =>
  pixelGrid(seed, (y, r) => {
    const grassy = y === 0 || (y === 1 && r < 0.7) || (y === 2 && r < 0.18);
    if (grassy) return r < 0.55 ? 1 : 3;
    return 10 + (r < 0.4 ? 0 : r < 0.66 ? 1 : r < 0.86 ? 2 : 3);
  });
const LEFT_FACE = sideFace(31);
const RIGHT_FACE = sideFace(53);

const Face = ({ cells, pal, matrix, shade }: { cells: number[][]; pal: { grass: readonly string[]; dirt: readonly string[] }; matrix: string; shade: number }) => (
  <g transform={`matrix(${matrix})`}>
    {cells.map((row, y) =>
      row.map((c, x) => (
        <rect key={`${x}-${y}`} x={x} y={y} width={1.04} height={1.04} fill={c >= 10 ? pal.dirt[c - 10] : pal.grass[c]} />
      )),
    )}
    {shade > 0 && <rect width={8} height={8} fill="#000" opacity={shade} />}
  </g>
);

/** Isometrischer Grasblock: Oberseite hell, linke Seite leicht, rechte stärker abgedunkelt. */
export const GrassBlock = ({ size, variant = 'java', className = '' }: { size: number; variant?: 'java' | 'bedrock'; className?: string }) => {
  const pal = PALETTES[variant];
  return (
    <svg viewBox="0 0 64 64" width={size} height={size} className={className} aria-hidden="true">
      <Face cells={TOP_FACE} pal={pal} matrix="3.5 1.75 -3.5 1.75 32 3" shade={0} />
      <Face cells={LEFT_FACE} pal={pal} matrix="3.5 1.75 0 3.5 4 17" shade={0.14} />
      <Face cells={RIGHT_FACE} pal={pal} matrix="3.5 -1.75 0 3.5 32 31" shade={0.32} />
    </svg>
  );
};

/**
 * Lädt das Server-Icon (eigenes Icon oder Modpack-Icon) als Blob-URL.
 * status 'error' = kein Icon gesetzt -> Standard-Glyph anzeigen. Nur in
 * observer-Komponenten nutzen (iconVersions lädt nach einem Upload neu).
 */
export function useServerIconUrl(serverId: string) {
  const [iconUrl, setIconUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const blobUrlRef = useRef<string | null>(null);

  // Version bump forces a re-fetch after an icon upload.
  const version = serverStore.iconVersions.get(serverId) || 0;

  useEffect(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    setIconUrl(null);
    setStatus('loading');

    let isMounted = true;
    const loadIcon = async () => {
      try {
        const res = await apiFetch(`/api/servers/${serverId}/icon?v=${version}`);
        if (!isMounted) return;

        // The endpoint returns JSON { noIcon: true } (200) when nothing is set —
        // only treat actual images as a real icon.
        const contentType = res.headers.get('content-type') || '';
        if (res.ok && contentType.startsWith('image/')) {
          const blob = await res.blob();
          if (!isMounted) return;
          const url = URL.createObjectURL(blob);
          blobUrlRef.current = url;
          setIconUrl(url);
          setStatus('success');
        } else {
          setStatus('error');
        }
      } catch {
        if (isMounted) setStatus('error');
      }
    };
    loadIcon();

    return () => {
      isMounted = false;
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, [serverId, version]);

  return { iconUrl, status, setStatus };
}

export const ServerIcon: React.FC<ServerIconProps> = observer(({
  serverId,
  type,
  size = 32,
  className = '',
}) => {
  const { iconUrl, status, setStatus } = useServerIconUrl(serverId);

  if (status === 'loading') {
    return (
      <div className={`flex items-center justify-center bg-surface-2 rounded-lg animate-pulse ${className}`} style={{ width: size, height: size }}>
        <Loader2 size={size * 0.5} className="text-fg-muted animate-spin" />
      </div>
    );
  }

  // No custom/modpack image → a game-family default glyph.
  if (status === 'error' || !iconUrl) {
    if (type === undefined) {
      return (
        <div className={`flex items-center justify-center bg-surface-2 rounded-lg ${className}`} style={{ width: size, height: size }}>
          <ServerIco size={size * 0.6} className="text-fg-muted" />
        </div>
      );
    }
    return <GameGlyph type={type} size={size} className={className} />;
  }

  return (
    <img
      src={iconUrl}
      alt="Server Icon"
      className={`rounded-lg object-cover ${className}`}
      style={{ width: size, height: size }}
      onError={() => setStatus('error')}
    />
  );
});
